import { useForceUpdate } from "@prismicio/editor-support/React"
import { isDefined } from "@prismicio/editor-support/Type"
import { clsx } from "clsx"
import {
  createContext,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

import type { SX } from "../../theme/new"
import { CheckerBoard } from "../CheckerBoard"
import { Icon, type IconName } from "../Icon"
import styles from "./Carousel.module.css"

export interface CarouselProps<ITEM> {
  /* The list of data items to be rendered. */
  items: readonly ITEM[]
  /* The renderer for the current item. */
  children: (item: ITEM, page: number) => ReactNode
  /* A function used to teach Carousel how to read the image source of an item. */
  getItemSource: (item: ITEM) => string | undefined
  /* The page (as an index of the items array) to be displayed. */
  page: number
  /* Called when the page changed due to user interaction. */
  onPageChange: (page: number) => void
  style?: CSSProperties
}

type Direction = "idle" | "prev" | "next"

export function Carousel<ITEM>(props: CarouselProps<ITEM>): ReactElement {
  const { items, children, getItemSource, page, onPageChange, ...otherProps } =
    props

  const item = items[page]

  const itemSources = useMemo(
    () => items.map(getItemSource),
    [items, getItemSource]
  )
  const [direction, setDirection] = useDirection(itemSources)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => prefetchImages(itemSources), [itemSources])

  function onPrevious() {
    setDirection("prev")
    onPageChange(page === 0 ? items.length - 1 : page - 1)
  }

  function onNext() {
    setDirection("next")
    onPageChange(page === items.length - 1 ? 0 : page + 1)
  }

  return (
    <CheckerBoard>
      <CarouselContext.Provider value={{ direction }}>
        <div
          {...otherProps}
          className={clsx(styles.root)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {item ? children(item, page) : null}

          {isHovered && items.length > 1 && (
            <>
              <CarouselButton
                sx={{ left: 16 }}
                iconName="arrowBack"
                onClick={onPrevious}
              />
              <CarouselButton
                sx={{ right: 16 }}
                iconName="arrowForward"
                onClick={onNext}
              />
            </>
          )}
        </div>
      </CarouselContext.Provider>
    </CheckerBoard>
  )
}

function prefetchImages(sources: (string | undefined)[]) {
  sources.filter(isDefined).forEach((src) => (new Image().src = src))
}

function useDirection(itemSources: (string | undefined)[]) {
  const direction = useRef<Direction>("idle")
  const forceUpdate = useForceUpdate()
  const itemSourcesHash = itemSources.join("\n")
  const previousHash = useRef(itemSourcesHash)

  function setDirection(value: Direction) {
    direction.current = value
    forceUpdate()
  }

  // Reverts to the neutral/idle animation when the item sources changed.
  // This ensures the idle animation is played when the current item updates its src.
  if (previousHash.current !== itemSourcesHash) {
    previousHash.current = itemSourcesHash
    direction.current = "idle"
  }

  return [direction.current, setDirection] as const
}

interface CarouselButtonProps {
  sx?: SX
  iconName: IconName
  onClick: () => void
}

function CarouselButton(props: CarouselButtonProps) {
  const { sx, iconName, ...otherProps } = props

  return (
    <button {...otherProps} className={styles.button} style={sx}>
      <Icon className={styles.buttonIcon} name={iconName} size="medium" />
    </button>
  )
}

interface CarouselContext {
  direction: Direction
}

const CarouselContext = createContext<CarouselContext | null>(null)

export function useCarousel() {
  const context = useContext(CarouselContext)

  if (!context)
    throw new Error(
      "useCarousel must be used within a CarouselContext provider"
    )

  return context
}
